// Tenant Admin API - Maps to backend/src/tenant_admin/
// Admin-only endpoints for managing which users belong to which customer/tenant.

import { apiClient } from './client';

/**
 * List all customers (tenants).
 * @returns {Promise<Array<{customer_id:string, display_name:string}>>}
 */
export const listCustomers = async () => {
  try {
    const response = await apiClient.get('/admin/tenants/customers');
    return response.data.customers;
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Failed to list customers';
    throw new Error(errorMsg);
  }
};

/**
 * List the users belonging to a given customer.
 * @param {string} customerId
 * @returns {Promise<Array<{user_id:string, email:string}>>}
 */
export const listCustomerUsers = async (customerId) => {
  try {
    const response = await apiClient.get(`/admin/tenants/customers/${encodeURIComponent(customerId)}/users`);
    return response.data.users;
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Failed to list customer users';
    throw new Error(errorMsg);
  }
};

/**
 * Add a user (by email) to a customer.
 * @param {string} customerId
 * @param {string} email
 */
export const addCustomerUser = async (customerId, email) => {
  try {
    await apiClient.post(`/admin/tenants/customers/${encodeURIComponent(customerId)}/users`, { email });
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Failed to add user';
    throw new Error(errorMsg);
  }
};

/**
 * Search Supabase Auth users by email substring for the "add user"
 * autocomplete, excluding users already belonging to this customer.
 * @param {string} customerId
 * @param {string} query
 * @returns {Promise<Array<{user_id:string, email:string}>>}
 */
export const searchCustomerUsers = async (customerId, query) => {
  try {
    const response = await apiClient.get(
      `/admin/tenants/customers/${encodeURIComponent(customerId)}/users/search`,
      { params: { q: query } },
    );
    return response.data.users;
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Failed to search users';
    throw new Error(errorMsg);
  }
};

/**
 * Remove a user from a customer.
 * @param {string} customerId
 * @param {string} userId
 */
export const removeCustomerUser = async (customerId, userId) => {
  try {
    await apiClient.delete(`/admin/tenants/customers/${encodeURIComponent(customerId)}/users/${encodeURIComponent(userId)}`);
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Failed to remove user';
    throw new Error(errorMsg);
  }
};

export const getCustomerOverview = async (customerId) => {
  try {
    const response = await apiClient.get(`/admin/tenants/customers/${encodeURIComponent(customerId)}/overview`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to load tenant overview');
  }
};

export const getCustomerUserStates = async (customerId) => {
  try {
    const response = await apiClient.get(`/admin/tenants/customers/${encodeURIComponent(customerId)}/user-states`);
    return response.data.users;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to load user dashboard states');
  }
};

export const setAllowedComponents = async (customerId, allowedComponentIds) => {
  try {
    const response = await apiClient.put(
      `/admin/tenants/customers/${encodeURIComponent(customerId)}/allowed-components`,
      { allowed_component_ids: allowedComponentIds }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to save tile access');
  }
};

export const listAssignableSharedConfigs = async () => {
  try {
    const response = await apiClient.get('/admin/tenants/shared-configs');
    return response.data.configs;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to load shared configs');
  }
};

export const assignConfigToUser = async (customerId, userId, configId, configName) => {
  try {
    const response = await apiClient.post(
      `/admin/tenants/customers/${encodeURIComponent(customerId)}/users/${encodeURIComponent(userId)}/assign-config`,
      { config_id: configId, config_name: configName }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to assign config');
  }
};

export const assignConfigToCustomer = async (customerId, configId, configName) => {
  try {
    const response = await apiClient.post(
      `/admin/tenants/customers/${encodeURIComponent(customerId)}/assign-config`,
      { config_id: configId, config_name: configName }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to assign config to tenant');
  }
};

export const getBoardSummary = async () => {
  try {
    const response = await apiClient.get('/admin/tenants/customers/board-summary');
    return response.data.tenants;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to load tenant board summary');
  }
};

export const createCustomer = async (customerId, displayName) => {
  try {
    const response = await apiClient.post('/admin/tenants/customers', {
      customer_id: customerId,
      display_name: displayName,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to create tenant');
  }
};

export const setOnboardingStep = async (customerId, step, done, note) => {
  try {
    const response = await apiClient.put(
      `/admin/tenants/customers/${encodeURIComponent(customerId)}/onboarding-status`,
      { step, done, note },
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to update onboarding step');
  }
};

export const getSentryIssues = async (customerId) => {
  try {
    const response = await apiClient.get(`/admin/tenants/customers/${encodeURIComponent(customerId)}/sentry-issues`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to load Sentry issues');
  }
};

/** Clear only this tenant's cache sub-store (Overview tab action). */
export const clearTenantCache = async (customerId) => {
  try {
    await apiClient.post(`/admin/tenants/customers/${encodeURIComponent(customerId)}/cache/clear`);
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to clear cache');
  }
};

/**
 * This tenant's own OCEL files, for the Overview tab's activate-file picker.
 * @param {string} customerId
 * @returns {Promise<Array<{file_type:string, display_name:?string}>>}
 */
export const listCustomerFiles = async (customerId) => {
  try {
    const response = await apiClient.get(`/admin/tenants/customers/${encodeURIComponent(customerId)}/files`);
    return response.data.files;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to list tenant files');
  }
};

/** Activate fileType into this tenant's active-file slot only (never the global default). */
export const activateCustomerFile = async (customerId, fileType) => {
  try {
    const response = await apiClient.post(
      `/admin/tenants/customers/${encodeURIComponent(customerId)}/active-file`,
      { file_type: fileType },
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to activate file');
  }
};

/**
 * Admin-side write of a user's "always use latest scheduled data" flag —
 * the same auto_activate_latest_etl_file a user can otherwise only set for
 * themselves via the sidebar checkbox (AppSidebar.jsx).
 */
export const setUserAutoActivate = async (customerId, userId, enabled) => {
  try {
    await apiClient.put(
      `/admin/tenants/customers/${encodeURIComponent(customerId)}/users/${encodeURIComponent(userId)}/auto-activate`,
      { enabled },
    );
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to update auto-activate setting');
  }
};

// --- Admins group: role editing (not tenant-scoped) ---

/** Every user with the admin flag set. */
export const listAdmins = async () => {
  try {
    const response = await apiClient.get('/admin/tenants/admins');
    return response.data.admins;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to list admins');
  }
};

/** Grant admin privileges to an existing Supabase user by email. */
export const grantAdmin = async (email) => {
  try {
    await apiClient.post('/admin/tenants/admins', { email });
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to grant admin access');
  }
};

/** Revoke admin privileges from a user (the backend blocks revoking your own). */
export const revokeAdmin = async (userId) => {
  try {
    await apiClient.delete(`/admin/tenants/admins/${encodeURIComponent(userId)}`);
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to revoke admin access');
  }
};

// --- Tenant deactivation (soft delete) ---

/** Deactivate a tenant (hides it from the default list, blocks new
 * admin-console-driven user-adds/file-activations). Does not touch
 * existing users' real access or ETL infra. */
export const deactivateCustomer = async (customerId) => {
  try {
    const response = await apiClient.post(`/admin/tenants/customers/${encodeURIComponent(customerId)}/deactivate`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to deactivate tenant');
  }
};

/** Reactivate a previously deactivated tenant. */
export const reactivateCustomer = async (customerId) => {
  try {
    const response = await apiClient.post(`/admin/tenants/customers/${encodeURIComponent(customerId)}/reactivate`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to reactivate tenant');
  }
};

// --- Global cache management ---

/** Cache stats aggregated across the shared store and every tenant sub-store. */
export const getGlobalCacheStats = async () => {
  try {
    const response = await apiClient.get('/admin/tenants/cache/global-stats');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to load cache stats');
  }
};

/** Clear the shared cache store AND every tenant sub-store. */
export const clearAllCaches = async () => {
  try {
    await apiClient.post('/admin/tenants/cache/clear-all');
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to clear caches');
  }
};

// --- Audit log viewer ---

/**
 * @param {{customerId?: string, eventType?: string, limit?: number}} [filters]
 */
export const getAuditLog = async ({ customerId, eventType, limit } = {}) => {
  try {
    const response = await apiClient.get('/admin/tenants/audit-log', {
      params: {
        ...(customerId ? { customer_id: customerId } : {}),
        ...(eventType ? { event_type: eventType } : {}),
        ...(limit ? { limit } : {}),
      },
    });
    return response.data.entries;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Failed to load audit log');
  }
};
